import type {
  TenantsResponse,
  MetricsResponse,
  MetricsParams,
  JobsResponse,
  JobsParams,
  JobDetailResponse,
  JobStatsResponse,
  SearchResponse,
  SearchParams,
  EventsParams,
  ChartsResponse,
  ApiError,
} from './types';
import { signRequest, isIamAuthEnabled, getApiEndpoint } from './awsAuth';
import { validateApiParams, sanitizeSearchQuery, isValidId } from '../utils/validation';

// Use configured API endpoint or fallback to local proxy
// Strip trailing slash to avoid double-slash in URL construction (endpoints start with /)
const API_BASE = (getApiEndpoint() || '/api').replace(/\/+$/, '');

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE}${endpoint}`;

    let fetchOptions: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    // Sign request with SigV4 if IAM auth is enabled
    if (isIamAuthEnabled()) {
      try {
        fetchOptions = await signRequest(url, fetchOptions);
      } catch (error) {
        console.error('Failed to sign request:', error);
        throw new Error('Authentication failed. Please check your AWS credentials.');
      }
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      const responseText = await response.text();
      console.error(`API ${response.status} ${url}:`, responseText);
      let error: ApiError;
      try {
        error = JSON.parse(responseText);
      } catch {
        error = {
          error: 'UnknownError',
          message: responseText || response.statusText,
          statusCode: response.status,
        };
      }
      throw error;
    }

    return response.json();
  }

  private buildQueryString(params: object): string {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    }
    const qs = searchParams.toString();
    return qs ? `?${qs}` : '';
  }

  // Tenants
  async getTenants(): Promise<TenantsResponse> {
    return this.request<TenantsResponse>('/tenants');
  }

  // Metrics
  async getMetrics(params: MetricsParams): Promise<MetricsResponse> {
    validateApiParams(params);
    const qs = this.buildQueryString(params);
    return this.request<MetricsResponse>(`/metrics${qs}`);
  }

  // Jobs
  async getJobs(params: JobsParams): Promise<JobsResponse> {
    validateApiParams(params);
    const qs = this.buildQueryString(params);
    return this.request<JobsResponse>(`/jobs${qs}`);
  }

  async getJobDetail(
    jobId: string,
    tenantId: string,
    includeLogs = false
  ): Promise<JobDetailResponse> {
    if (!isValidId(jobId)) {
      throw new Error('Invalid job ID format');
    }
    validateApiParams({ tenant_id: tenantId });
    const qs = this.buildQueryString({ tenant_id: tenantId, include_logs: includeLogs });
    return this.request<JobDetailResponse>(`/jobs/${encodeURIComponent(jobId)}${qs}`);
  }

  async getJobStats(jobId: string, tenantId: string): Promise<JobStatsResponse> {
    if (!isValidId(jobId)) {
      throw new Error('Invalid job ID format');
    }
    validateApiParams({ tenant_id: tenantId });
    const qs = this.buildQueryString({ tenant_id: tenantId });
    return this.request<JobStatsResponse>(`/jobs/${encodeURIComponent(jobId)}/stats${qs}`);
  }

  // Search
  async search(params: SearchParams): Promise<SearchResponse> {
    validateApiParams({ tenant_id: params.tenant_id });
    const sanitizedParams = {
      ...params,
      query: sanitizeSearchQuery(params.query),
    };
    return this.request<SearchResponse>('/search', {
      method: 'POST',
      body: JSON.stringify(sanitizedParams),
    });
  }

  // Events
  async getEvents(params: EventsParams): Promise<{ events: unknown[]; pagination: { page: number; page_size: number; has_more: boolean } }> {
    validateApiParams(params);
    const qs = this.buildQueryString(params);
    return this.request(`/events${qs}`);
  }

  // Namespaces
  async getNamespaces(params: { tenant_id: string; time_range?: string }): Promise<{ namespaces: string[] }> {
    validateApiParams(params);
    const qs = this.buildQueryString(params);
    return this.request(`/namespaces${qs}`);
  }

  // Charts
  async getCharts(params: { tenant_id: string; time_range?: string }): Promise<ChartsResponse> {
    validateApiParams(params);
    const qs = this.buildQueryString(params);
    return this.request<ChartsResponse>(`/charts${qs}`);
  }
}

export const apiClient = new ApiClient();
export default apiClient;
